import type { SocialAdResponseDto } from '@/apis/backend/generated/models';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { paths } from '@/routes/paths';
import { useOrganization } from '@/context/organization-context';
import { useNavigate, createFileRoute } from '@tanstack/react-router';
import {
  useSocialAdsControllerListAds,
  useSocialAdsControllerDeleteAd,
} from '@/apis/backend/generated/social-ads/social-ads';

import {
  Add as AddIcon,
  Edit as EditIcon,
  Image as ImageIcon,
  Delete as DeleteIcon,
  MoreVert as MoreIcon,
} from '@mui/icons-material';
import {
  Box,
  Grid,
  Card,
  Chip,
  Menu,
  alpha,
  Button,
  Skeleton,
  Snackbar,
  MenuItem,
  useTheme,
  CardMedia,
  Container,
  Typography,
  IconButton,
  CardContent,
  CardActions,
  ListItemIcon,
  ListItemText,
} from '@mui/material';

export const Route = createFileRoute('/_authed/dashboard/o/$orgId/c/$clientId/social-ads/')({
  component: SocialAdsPage,
});

function SocialAdsPage() {
  const { t } = useTranslation('socialAds');
  const theme = useTheme();
  const navigate = useNavigate();
  const { orgId, clientId } = Route.useParams();
  const tp = paths.dashboard.tenant(orgId, clientId);
  const { activeOrganization, activeClient } = useOrganization();
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const [platformFilter, setPlatformFilter] = useState<'all' | 'instagram' | 'facebook'>('all');

  const {
    data: adsData,
    isLoading,
  } = useSocialAdsControllerListAds(
    activeOrganization?.id || '',
    activeClient?.id || '',
    {
      limit: 50,
      onlyMasters: true,
      ...(platformFilter !== 'all' ? { platform: platformFilter } : {}),
    },
    {
      query: {
        enabled: !!activeOrganization?.id && !!activeClient?.id,
      },
    },
  );

  const ads = adsData?.ads || [];
  const total = adsData?.total || 0;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h3" fontWeight="bold">
            {t('socialAds.title', 'Social Ads')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('socialAds.subtitle', 'AI-generated social media creatives for Instagram and Facebook')}
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<ImageIcon />}
            onClick={() => navigate({ to: tp.socialAdsCreate })}
            sx={{
              px: 3,
              py: 1.25,
              borderRadius: 3,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {t('socialAds.browseTemplates', 'Browse Templates')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate({ to: tp.socialAdsCreate })}
            sx={{
              px: 3,
              py: 1.25,
              borderRadius: 3,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {t('socialAds.createAd', 'Create Ad')}
          </Button>
        </Box>
      </Box>

      {/* Loading */}
      {isLoading && (
        <Grid container spacing={3}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={i}>
              <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 2 }} />
              <Skeleton variant="text" sx={{ mt: 1 }} />
              <Skeleton variant="text" width="60%" />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Empty State */}
      {!isLoading && ads.length === 0 && (
        <Box
          sx={{
            textAlign: 'center',
            py: 10,
            px: 4,
            borderRadius: 4,
            bgcolor: alpha(theme.palette.primary.main, 0.03),
            border: `1px dashed ${alpha(theme.palette.primary.main, 0.2)}`,
          }}
        >
          <ImageIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h5" fontWeight={600} gutterBottom>
            {t('socialAds.empty.title', 'No social ads yet')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 500, mx: 'auto' }}>
            {t('socialAds.empty.body', 'Create your first AI-generated social ad. Just describe what you want and pick a platform size.')}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate({ to: tp.socialAdsCreate })}
            sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 600, px: 4 }}
          >
            {t('socialAds.empty.cta', 'Create Your First Ad')}
          </Button>
        </Box>
      )}

      {/* Filter bar */}
      {!isLoading && ads.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t('socialAds.adCount', '{{count}} ad', { count: total })}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {(['all', 'instagram', 'facebook'] as const).map((p) => (
              <Button
                key={p}
                size="small"
                variant={platformFilter === p ? 'contained' : 'text'}
                onClick={() => setPlatformFilter(p)}
                sx={{ textTransform: 'capitalize', borderRadius: 2 }}
              >
                {t(`socialAds.platform.${p}`, p)}
              </Button>
            ))}
          </Box>
        </Box>
      )}

      {/* Ads Grid */}
      {!isLoading && ads.length > 0 && (
        <Grid container spacing={3}>
            {ads.map((ad) => (
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={ad.id}>
                <AdCard
                  ad={ad}
                  onSnackbar={(msg) => setSnackbar({ open: true, message: msg })}
                />
              </Grid>
            ))}
          </Grid>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Container>
  );
}

function AdCard({
  ad,
  onSnackbar,
}: {
  ad: SocialAdResponseDto;
  onSnackbar: (msg: string) => void;
}) {
  const { t } = useTranslation('socialAds');
  const theme = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { orgId, clientId } = Route.useParams();
  const base = paths.dashboard.tenantRoot(orgId, clientId);
  const { activeOrganization, activeClient } = useOrganization();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const { mutate: deleteAd } = useSocialAdsControllerDeleteAd({
    mutation: {
      onSuccess: () => {
        onSnackbar(t('socialAds.snackbar.deleted', 'Ad deleted'));
        queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey[0];
            return typeof key === 'string' && key.includes('/social-ads');
          },
        });
      },
      onError: () => {
        onSnackbar(t('socialAds.snackbar.deleteFailed', 'Failed to delete ad'));
      },
    },
  });

  const handleEdit = () => {
    setMenuAnchor(null);
    navigate({ to: `${base}/social-ads/${ad.id}` });
  };

  const handleDelete = () => {
    if (!activeOrganization?.id || !activeClient?.id) return;
    setMenuAnchor(null);
    deleteAd({
      orgId: activeOrganization.id,
      clientId: activeClient.id,
      id: ad.id,
    });
  };

  const createdDate = new Date(ad.createdAt).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <Card
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        transition: 'box-shadow 0.2s, transform 0.2s',
        cursor: 'pointer',
        '&:hover': {
          boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.1)}`,
          transform: 'translateY(-2px)',
        },
      }}
      onClick={handleEdit}
    >
      {ad.thumbnailUrl ? (
        <CardMedia
          component="img"
          image={ad.thumbnailUrl}
          alt={ad.name}
          sx={{
            height: 220,
            objectFit: 'cover',
            bgcolor: 'grey.100',
          }}
        />
      ) : (
        <Box
          sx={{
            height: 220,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(theme.palette.grey[200], 0.5),
          }}
        >
          <ImageIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
        </Box>
      )}

      <CardContent sx={{ pb: 1 }}>
        <Typography variant="subtitle1" fontWeight={600} noWrap>
          {ad.name}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            {createdDate}
          </Typography>
          <Chip
            label={ad.status}
            size="small"
            variant="outlined"
            color={ad.status === 'exported' ? 'success' : 'default'}
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
          {ad.platform && (
            <Chip
              label={ad.platform}
              size="small"
              variant="outlined"
              sx={{ height: 20, fontSize: '0.7rem', textTransform: 'capitalize' }}
            />
          )}
          {typeof ad.variantCount === 'number' && ad.variantCount > 0 && (
            <Chip
              label={t('socialAds.variantSizes', '+{{count}} sizes', { count: ad.variantCount })}
              size="small"
              variant="outlined"
              color="primary"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
          <Typography variant="caption" color="text.secondary">
            {ad.adSizeId}
          </Typography>
        </Box>
      </CardContent>

      <CardActions sx={{ px: 2, pb: 1.5, pt: 0, justifyContent: 'space-between' }}>
        <Button
          size="small"
          startIcon={<EditIcon />}
          onClick={(e) => {
            e.stopPropagation();
            handleEdit();
          }}
          sx={{ textTransform: 'none', fontWeight: 500 }}
        >
          {t('common:edit')}
        </Button>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setMenuAnchor(e.currentTarget);
          }}
        >
          <MoreIcon fontSize="small" />
        </IconButton>
      </CardActions>

      <Menu
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={(e: any) => {
          e?.stopPropagation?.();
          setMenuAnchor(null);
        }}
      >
        <MenuItem onClick={(e) => {
          e.stopPropagation();
          handleEdit();
        }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t('socialAds.menu.editInCanvas', 'Edit in Canvas')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={(e) => {
          e.stopPropagation();
          handleDelete();
        }}>
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>{t('common:delete')}</ListItemText>
        </MenuItem>
      </Menu>
    </Card>
  );
}
